import React, { useEffect, useState, useContext } from 'react'
import { Table, Icon } from 'antd';
import {
    FormSelect,
    OptionProps,
    Widget,
    FormContext,
    Button,
    ColumnProps,
    notification,
} from '@digital/ui';
import {
    CrfAcronimoAgrupador,
    getAllAcronimosAgrupador
} from '@app/api/crf/acronimo/Agrupador/acronimosAgrupador';
import {
    getAllDisciplinasOptions,
    getAllTiposOptions
} from '@app/api/crf/acronimo/Agrupador/tipoAgrupador';
import {
    ICrfContaContabilAgrupador,
    getAllCrfContaContabil,
    updateCrfContaContabil,
} from '@app/api/crf/contaContabil';
import { isEmpty, isNotEmpty } from '@app/helpers/string';
import { compareText, compareTypeNumber } from '@app/helpers/comparators';
import { copyArrayRecursive } from '@app/helpers/array';
import { mountFormFilterDropDown } from '@app/components/FormFilterDropDown';




const CrfContaContabilTable = () => {

    const { formProps } = useContext(FormContext);

    const [source, setSource] = useState<ICrfContaContabilAgrupador[]>([]);
    const [sourceBkp, setSourceBkp] = useState<ICrfContaContabilAgrupador[]>([]);

    const [siglas, setSiglas] = useState<CrfAcronimoAgrupador[]>([]);
    const [siglasOptions, setSiglasOptions] = useState<OptionProps[]>([]);
    const [disciplinasOptions, setDisciplinasOptions] = useState<OptionProps[]>([]);
    const [tiposOptions, setTiposOptions] = useState<OptionProps[]>([]);

    const [siglasOptionsFilter, setSiglasOptionsFilter] = useState<OptionProps[]>([]);

    const [editingKey, setEditingKey] = useState<string>('');

    const isEditing = (record: ICrfContaContabilAgrupador) =>
        editingKey && editingKey == record.contaContabilId;

    const changeEditKey = (key: string) => {
        if (isNotEmpty(editingKey) && key !== editingKey) {
            resetLineByKey(editingKey);
            resetLineByKey(key);
        }
        setEditingKey(key);
    };

    const fetchData = () => {
        const getDisciplinas = async () => {
            const result = await getAllDisciplinasOptions();
            if (result) {
                setDisciplinasOptions(result);
            };
        };

        const getTipos = async () => {
            const result = await getAllTiposOptions();
            if (result) {
                setTiposOptions(result);
            };
        };

        const getAcronimosAgrupador = async () => {
            const result = await getAllAcronimosAgrupador();
            if (result) {
                setSiglas(result);
                setSiglasOptions((result || []).map((v) => ({ text: v.sigla, value: v.id.toString() })));
                setSiglasOptionsFilter((result || []).map((v) => ({ text: v.sigla, value: v.id.toString() })));
            };
        };

        const fetchAllCrfContaContabilAgrupador = async () => {
            const all = await getAllCrfContaContabil();
            if (all) {
                setSource(all);
                setSourceBkp(copyArrayRecursive(all));
                setEditingKey('');
            };
        }

        getDisciplinas();
        getTipos();
        getAcronimosAgrupador();
        fetchAllCrfContaContabilAgrupador();
    };

    useEffect(() => fetchData(), []);

    const resetLineByKey = (key: string) => {
        const recordInSourceBkp = sourceBkp.find(x => x.contaContabilId == key);

        const recordInSource = source.find(x => x.contaContabilId == key);
        if (recordInSource && recordInSourceBkp) {
            if (isEmpty(recordInSourceBkp.acronimoAgrupadorId)) {
                recordInSource.disciplinaAgrupadorId = '';
                recordInSource.tipoAgrupadorTipoId = '';
                recordInSource.acronimoAgrupadorId = '';
            } else {
                recordInSource.disciplinaAgrupadorId = recordInSourceBkp.disciplinaAgrupadorId;
                recordInSource.tipoAgrupadorTipoId = recordInSourceBkp.tipoAgrupadorTipoId;
                recordInSource.acronimoAgrupadorId = recordInSourceBkp.acronimoAgrupadorId;
            }
            let tempSource = source.filter(x => x.contaContabilId != key);
            tempSource.push(recordInSource);
            setSource(tempSource);
            setEditingKey('');
        }

        setSiglasOptionsFilter(siglasOptions);
    }

    const resetFields = (record?: ICrfContaContabilAgrupador) => {
        if (record && isEmpty(record.acronimoAgrupadorId)) {
            resetLineByKey(record.contaContabilId);
        }

        setEditingKey('');

        formProps && formProps.resetForm();
    }

    const handleClickConfirm = (record: ICrfContaContabilAgrupador) => {
        if (isEmpty(record.acronimoAgrupadorId)) {
            notification.error('Selecione uma sigla');
            return;
        }

        updateCrfContaContabil({
            acronimoAgrupadorId: record.acronimoAgrupadorId,
            contaContabilId: record.contaContabilId,
        }).then(() => {
            fetchData();
        });
    }

    const handleClickCancel = (record: ICrfContaContabilAgrupador) => {
        resetFields(record);
    }

    const handleClickDelete = (record: ICrfContaContabilAgrupador) => {
        updateCrfContaContabil({
            contaContabilId: record.contaContabilId,
            removeAcronimoAgrupador: true,
        }).then(() => {
            fetchData();
        });
    }


    const columns = [
        {
            title: 'Conta Contábil',
            dataIndex: 'codigo',
            width: '10%',
            ...mountFormFilterDropDown("codigo", "Pesquisar por código"),
            sorter: (a, b) => compareTypeNumber(a.codigo, b.codigo),
        },
        {
            title: 'Descrição',
            dataIndex: 'descricao',
            width: '10%',
            ...mountFormFilterDropDown("descricao", "Pesquisar por descrição"),
            sorter: (a, b) => compareText(a.descricao, b.descricao),
        },
        {
            title: 'Sigla Agrupador',
            dataIndex: 'acronimoAgrupadorId',
            options: siglasOptionsFilter || siglasOptions,
            width: '20%',
            onCellClick: (record: ICrfContaContabilAgrupador, event: any) => {
                changeEditKey(record.contaContabilId);
            },
            render: (_: string, record: ICrfContaContabilAgrupador) => {
                const option: any = siglasOptions && siglasOptions.find((f: any) => f.value === record.acronimoAgrupadorId);

                return isEditing(record) ? (
                    <FormSelect
                        name="acronimoAgrupadorId"
                        options={siglasOptionsFilter || siglasOptions}
                        value={option ? option.value : ''}
                        onChange={(e: any) => {
                            record.acronimoAgrupadorId = e;
                            onChangeSelect(record, 'acronimoAgrupadorId');
                        }}
                        required
                        disabled={!isEditing(record)}
                    // allowClear
                    />
                ) : (
                    <div style={{ textAlign: 'left' }} >
                        <span>{option ? option.text : ''}<br /></span>
                    </div>
                );
            }
        },
        {
            title: 'Disciplina Agrupador',
            dataIndex: 'disciplinaAgrupadorId',
            key: 'disciplinaAgrupadorId',
            options: disciplinasOptions,
            width: '20%',
            onCellClick: (record: ICrfContaContabilAgrupador, event: any) => {
                changeEditKey(record.contaContabilId);
            },
            render: (_: string, record: ICrfContaContabilAgrupador) => {
                const option: any = disciplinasOptions && disciplinasOptions.find((f: any) => f.value === record.disciplinaAgrupadorId);

                return isEditing(record) ? (
                    <FormSelect
                        name="disciplinaAgrupadorId"
                        options={disciplinasOptions}
                        value={option ? option.text : ''}
                        required
                        onChange={(e: any) => {
                            record.disciplinaAgrupadorId = e;
                            onChangeSelect(record, 'disciplinaAgrupadorId')
                        }}
                        disabled={!isEditing(record)}
                    // allowClear
                    />
                ) : (
                    <div style={{ textAlign: 'left' }} >
                        <span>{option ? option.text : ''}<br /></span>
                    </div>
                );
            }
        },
        {
            title: 'Tipo Agrupador',
            dataIndex: 'tipoAgrupadorTipoId',
            key: 'tipoAgrupadorTipoId',
            options: tiposOptions,
            width: '20%',
            onCellClick: (record: ICrfContaContabilAgrupador, event: any) => {
                changeEditKey(record.contaContabilId);
            },
            render: (_: string, record: ICrfContaContabilAgrupador) => {
                const option: any = tiposOptions && tiposOptions.find((f: any) => f.value === record.tipoAgrupadorTipoId);

                return isEditing(record) ? (
                    <FormSelect
                        name="tipoAgrupadorTipoId"
                        options={tiposOptions}
                        required
                        value={option ? option.value : ''}
                        disabled={!isEditing(record)}
                        onChange={(e: any) => {
                            record.tipoAgrupadorTipoId = e;
                            onChangeSelect(record, 'tipoAgrupadorTipoId');
                        }}
                    // allowClear
                    />
                ) : (
                    <div style={{ textAlign: 'left' }} >
                        <span>{option ? option.text : ''}<br /></span>
                    </div>
                );
            },
        },
        {
            title: 'Opções',
            width: '10%',
            render: (_: any, record: ICrfContaContabilAgrupador) =>
                <Button
                    type="link"
                    icon="science-minus"
                    onClick={() => handleClickDelete(record)}
                />
            ,
        },
        {
            title: 'Ações',
            width: '10%',
            render: (_: any, record: ICrfContaContabilAgrupador) => {
                return isEditing(record) ? (
                    <span>
                        <Button
                            type="link"
                            icon="commons-ok"
                            onClick={() => handleClickConfirm(record)}
                        />
                        <Button
                            type="link"
                            icon="commons-cancel"
                            onClick={() => handleClickCancel(record)}
                        />
                    </span>
                ) : (<></>);
            },
        },
    ] as ColumnProps<ICrfContaContabilAgrupador>[];

    function onChangeSelect(record: ICrfContaContabilAgrupador, type: string) {

        let tipoAgrupadorTipoId = record.tipoAgrupadorTipoId;
        let disciplinaAgrupadorId = record.disciplinaAgrupadorId;

        let filterSigla: CrfAcronimoAgrupador[] = siglas || [];

        if (type === 'acronimoAgrupadorId' && isNotEmpty(record.acronimoAgrupadorId)) {
            let sigla = siglas.find((f: CrfAcronimoAgrupador) => f.id === record.acronimoAgrupadorId);
            if (sigla) {
                tipoAgrupadorTipoId = sigla.tipoId;
                disciplinaAgrupadorId = sigla.disciplinaId;
            }
        }


        if (disciplinaAgrupadorId && filterSigla) {
            filterSigla = filterSigla.filter((f: CrfAcronimoAgrupador) => f.disciplinaId === disciplinaAgrupadorId);
        }

        if (tipoAgrupadorTipoId && filterSigla) {
            filterSigla = filterSigla.filter((f: CrfAcronimoAgrupador) => f.tipoId === tipoAgrupadorTipoId);
        }

        let filterSiglaOptions = filterSigla
            .map((v) => ({ text: v.sigla, value: v.id.toString() }));

        setSiglasOptionsFilter(filterSiglaOptions);

        if (record && siglas && formProps) {
            switch (type) {
                case 'tipoAgrupadorTipoId':
                    record.acronimoAgrupadorId = '';
                    // record.disciplinaAgrupadorId = '';

                    if (filterSigla && filterSigla.length == 1) {
                        formProps.setFieldValue('acronimoAgrupadorId', filterSigla[0].id);
                        record.acronimoAgrupadorId = filterSigla[0].id;
                        record.disciplinaAgrupadorId = filterSigla[0].disciplinaId;
                        record.tipoAgrupadorTipoId = filterSigla[0].tipoId;
                    }
                    break;
                case 'disciplinaAgrupadorId':
                    record.acronimoAgrupadorId = '';
                    // record.tipoAgrupadorTipoId = '';

                    if (filterSigla && filterSigla.length == 1) {
                        formProps.setFieldValue('acronimoAgrupadorId', filterSigla[0].id);
                        record.acronimoAgrupadorId = filterSigla[0].id;
                        record.disciplinaAgrupadorId = filterSigla[0].disciplinaId;
                        record.tipoAgrupadorTipoId = filterSigla[0].tipoId;
                    }
                    break;
                case 'acronimoAgrupadorId':
                    resetFields();
                    formProps.setFieldValue('disciplinaAgrupadorId', disciplinaAgrupadorId);
                    formProps.setFieldValue('tipoAgrupadorTipoId', tipoAgrupadorTipoId);

                    record.disciplinaAgrupadorId = disciplinaAgrupadorId;
                    record.tipoAgrupadorTipoId = tipoAgrupadorTipoId;
                    break;
            }
        }
    }

    return (
        <Widget>
            <Table
                rowKey="id"
                dataSource={[].slice.call(source)
                    .sort((a: ICrfContaContabilAgrupador, b: ICrfContaContabilAgrupador) =>
                        compareTypeNumber(a.codigo, b.codigo)
                    )
                }
                columns={columns}
            />
        </Widget>
    )
}

export default CrfContaContabilTable;